/**
 * Reusable event-stream view.
 *
 * Scope filters come from two sources:
 *
 *   - `lockedScope` (props) — the entity the SubNav tab represents
 *     (e.g. PoolEvents passes `{ service_id, pool_id }`). Locked
 *     filters can never be removed by the operator: the SubNav tab
 *     title already says where they are, and removing them would
 *     widen the filter past the focused entity.
 *
 *   - URL `useSearchParams` — additional, removable filters layered
 *     on top of the locked set. A "View events for replica #42"
 *     link from inside the Pool > Replicas tab points at
 *     `?focus=pool:X&tab=events&replica_id=42`; the resulting
 *     stream is locked to (service, pool) but adds a removable
 *     `replica` chip so the operator can drop just that one filter
 *     without leaving the pool's Events tab.
 *
 * In Global mode (`lockedScope` omitted), every scope filter comes
 * from the URL and is removable.
 *
 * The `kind` and `min_severity` filters always live in component
 * state and are independent of the scope mode — operators reach for
 * them while reading either view.
 */
import { Fragment, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import {
  ChevronDown,
  ChevronRight,
  ListChecks,
  Search,
  Terminal,
  X,
} from "lucide-react";

import { api } from "@/api/client";
import { useErrorKindReference } from "@/api/local/errorKinds";
import type { EventRow, EventSeverity, Pool, Replica, Service } from "@/api/types";
import { Pill, type PillTone } from "@/components/Pill";
import { Section } from "@/components/Section";
import {
  EVENTS_FILTER_PARAMS,
  type EventsFilterParam,
} from "@/workloads/eventsHref";
import { useAllPools, useAllReplicas, useServices } from "@/workloads/data";

export interface EventsStreamScope {
  service_id?: number | null;
  pool_id?: number | null;
  replica_id?: number | null;
}

interface Props {
  /**
   * When provided, the stream is locked to this scope: URL scope
   * params are ignored and no removable chips are rendered.
   * Used by per-entity Events tabs (Service / Pool / Pod).
   */
  lockedScope?: EventsStreamScope;
}

export function EventsStream({ lockedScope }: Props) {
  const [params, setParams] = useSearchParams();
  const [kind, setKind] = useState("");
  const [sev, setSev] = useState<EventSeverity | "">("");
  // Inline expansion: click a row to reveal a `detail-row` underneath
  // with the pretty-printed payload. Multiple rows can stay open at
  // once so the operator can compare two events side by side.
  const [expanded, setExpanded] = useState<Set<number>>(() => new Set());

  const toggleExpanded = (id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Per-key resolution: locked wins, otherwise read from URL.
  // Each key remembers whether it's locked so the chip renderer
  // below can hide the × on locked chips (or skip the chip entirely
  // since the SubNav title already shows the locked entity).
  const lockedServiceId = lockedScope?.service_id ?? null;
  const lockedPoolId = lockedScope?.pool_id ?? null;
  const lockedReplicaId = lockedScope?.replica_id ?? null;

  const urlServiceId = numOrNull(params.get("service_id"));
  const urlPoolId = numOrNull(params.get("pool_id"));
  const urlReplicaId = numOrNull(params.get("replica_id"));

  const serviceId =
    lockedServiceId !== null ? lockedServiceId : urlServiceId;
  const poolId = lockedPoolId !== null ? lockedPoolId : urlPoolId;
  const replicaId =
    lockedReplicaId !== null ? lockedReplicaId : urlReplicaId;

  // Only URL-sourced scope keys can be removed — locked keys are
  // owned by the SubNav tab and removing them would widen the
  // filter past the focused entity.
  const serviceRemovable = lockedServiceId === null && urlServiceId !== null;
  const poolRemovable = lockedPoolId === null && urlPoolId !== null;
  const replicaRemovable = lockedReplicaId === null && urlReplicaId !== null;

  // Resolve display labels for the active scope chips. We deliberately
  // reuse the same TanStack queries the rest of the Workloads page
  // hits so the cache is warm and the chip labels never lag the tree.
  // Skipped entirely in locked mode — the SubNav tab title plus the
  // `nav` rail already tell the operator which entity they're on.
  const services = useServices();
  const replicas = useAllReplicas();
  const serviceIds = services.data?.map((s) => s.id);
  const pools = useAllPools(serviceIds);

  const serviceLabel = useMemo(
    () => labelForService(services.data, serviceId),
    [services.data, serviceId],
  );
  const poolLabel = useMemo(
    () => labelForPool(pools.data, poolId),
    [pools.data, poolId],
  );
  const replicaLabel = useMemo(
    () => labelForReplica(replicas.data, replicaId),
    [replicas.data, replicaId],
  );

  const removeFilter = (key: EventsFilterParam) => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(key);
        return next;
      },
      { replace: true },
    );
  };

  const clearAllScope = () => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const k of EVENTS_FILTER_PARAMS) next.delete(k);
        return next;
      },
      { replace: true },
    );
  };

  const apiParams = new URLSearchParams({ limit: "200" });
  if (kind) apiParams.set("kind", kind);
  if (sev) apiParams.set("min_severity", sev);
  if (serviceId !== null) apiParams.set("service_id", String(serviceId));
  if (poolId !== null) apiParams.set("pool_id", String(poolId));
  if (replicaId !== null) apiParams.set("replica_id", String(replicaId));

  const list = useQuery({
    queryKey: ["events", kind, sev, serviceId, poolId, replicaId],
    queryFn: () => api.get<EventRow[]>(`/events?${apiParams.toString()}`),
    refetchInterval: 10_000,
  });

  // Show the chip strip when at least one URL-sourced scope filter
  // is active (locked filters never get a chip — the SubNav already
  // tells the operator which entity they're on).
  const showRemovableChips =
    serviceRemovable || poolRemovable || replicaRemovable;

  return (
    <>
      <div className="toolbar" style={{ marginBottom: 12 }}>
        <div
          className="toolbar"
          style={{
            gap: 6,
            background: "var(--surface-2)",
            border: "1px solid var(--edge-strong)",
            borderRadius: "var(--radius-md)",
            padding: "2px 8px",
          }}
        >
          <Search size={14} className="muted" />
          <input
            placeholder="kind contains…"
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            style={{
              background: "transparent",
              border: "none",
              padding: "4px 0",
              width: 180,
            }}
          />
        </div>
        <select
          value={sev}
          onChange={(e) => setSev(e.target.value as EventSeverity | "")}
        >
          <option value="">any severity</option>
          {(["debug", "info", "warn", "error", "critical"] as const).map((s) => (
            <option key={s} value={s}>
              {s}+
            </option>
          ))}
        </select>
      </div>

      {/* Scope chips — pinned at the top so they're impossible to
          miss when the operator deep-links in from a per-entity
          button on another page. Each chip's × removes just that
          filter (other scope + kind + severity stay put).
          Locked scope keys (set by per-entity Events tabs via
          `lockedScope`) get no chip: the SubNav tab + entity title
          already say where the operator is, and an "× remove"
          action would be misleading because the locked filter
          can't be widened past the focused entity. */}
      {showRemovableChips && (
        <div
          className="toolbar"
          style={{
            gap: 6,
            marginBottom: 12,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <span
            className="muted"
            style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.6 }}
          >
            scope
          </span>
          {serviceRemovable && (
            <ScopeChip
              label="service"
              value={serviceLabel ?? `#${serviceId}`}
              onRemove={() => removeFilter("service_id")}
            />
          )}
          {poolRemovable && (
            <ScopeChip
              label="pool"
              value={poolLabel ?? `#${poolId}`}
              onRemove={() => removeFilter("pool_id")}
            />
          )}
          {replicaRemovable && (
            <ScopeChip
              label="replica"
              value={replicaLabel ?? `#${replicaId}`}
              onRemove={() => removeFilter("replica_id")}
            />
          )}
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={clearAllScope}
            title="Clear all URL-sourced scope filters"
          >
            clear scope
          </button>
        </div>
      )}

      {/* Quick filter chips for the new SLO/drain/xDS event kinds. The
          filter is a substring match so a chip just sets the search
          input to the kind prefix; clearing it brings everything back. */}
      <div
        className="toolbar"
        style={{
          gap: 6,
          marginBottom: 12,
          flexWrap: "wrap",
        }}
      >
        {KIND_FILTER_CHIPS.map((chip) => {
          const isActive = kind === chip.value;
          return (
            <button
              key={chip.value}
              type="button"
              className={`btn btn--sm ${isActive ? "btn--primary" : "btn--ghost"}`}
              onClick={() => setKind(isActive ? "" : chip.value)}
              title={chip.hint}
            >
              {chip.label}
            </button>
          );
        })}
        {kind && (
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => setKind("")}
            title="Clear the kind filter"
          >
            clear
          </button>
        )}
      </div>

      <Section
        title="Stream"
        icon={<ListChecks />}
        count={`${list.data?.length ?? 0}`}
        flush
      >
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th style={{ width: 28 }} aria-label="expand" />
                <th>Time</th>
                <th>Kind</th>
                <th>Severity</th>
                <th>Message</th>
              </tr>
            </thead>
            <tbody>
              {(list.data ?? []).map((e) => {
                const isOpen = expanded.has(e.id);
                const message = humanEventMessage(e);
                return (
                  <Fragment key={e.id}>
                    <tr
                      className="expandable"
                      onClick={() => toggleExpanded(e.id)}
                    >
                      <td className="muted">
                        {isOpen ? (
                          <ChevronDown size={14} />
                        ) : (
                          <ChevronRight size={14} />
                        )}
                      </td>
                      <td className="mono muted">
                        {new Date(e.ts).toLocaleString()}
                      </td>
                      <td className="mono">{e.kind}</td>
                      <td>
                        <span
                          style={{
                            display: "inline-flex",
                            gap: 4,
                            alignItems: "center",
                          }}
                        >
                          <Pill tone={severityTone(e.severity)}>
                            {e.severity}
                          </Pill>
                          <EventAttentionChip event={e} />
                        </span>
                      </td>
                      {/* Human-readable message first; the raw JSON
                          payload stays one click away in the expanded
                          detail. Events with an empty error string
                          still show at least their error_type so a
                          failure never renders as a blank row. */}
                      <td
                        className={message ? undefined : "mono muted"}
                        style={{
                          fontSize: message ? 12 : 11,
                          maxWidth: 0,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={message ?? undefined}
                      >
                        {message ?? JSON.stringify(e.payload)}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr key={`${e.id}-detail`} className="detail-row">
                        <td colSpan={5}>
                          <EventDetail event={e} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
              {!list.data?.length && (
                <tr>
                  <td colSpan={5} className="empty">
                    no events match the current filter
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Section>
    </>
  );
}

function EventDetail({ event }: { event: EventRow }) {
  // Surface the scope ids that live inside `payload` as first-class
  // kv rows so the operator doesn't have to scan the JSON for them.
  // Anything not pulled up here still appears in the pretty-printed
  // payload below.
  const p = event.payload ?? {};
  const serviceId = p["service_id"];
  const poolId = p["pool_id"];
  const poolName = p["pool_name"];
  const replicaId = p["replica_id"];
  const skyReplicaId = p["sky_replica_id"];
  const classification = p["classification"];
  const errorText = p["error"];
  const errorType = p["error_type"];
  const errorLocation = p["error_location"];
  const failureReason = p["failure_reason"];
  const hint = p["hint"];

  // Build the deep-link into Pool > Logs for the replica that this
  // event belongs to. Two preconditions must hold:
  //
  //   - We know the BackStage ``pool_id`` (target the per-pool
  //     SubNav's Logs tab).
  //   - We know the SkyServe ``sky_replica_id`` (the integer the
  //     controller keys ``sky.serve.tail_logs`` on — the BackStage
  //     ``replica_id`` is a different number from the per-service
  //     1..N that SkyPilot assigns).
  //
  // When ``error_location`` is also present we pre-fill the search
  // box so the operator lands directly on the matching traceback
  // frame in the raw log. Without it we still link, just without a
  // search query — the operator scrolls.
  const logsHref =
    typeof poolId === "number" && typeof skyReplicaId === "number"
      ? buildLogsHref(poolId, skyReplicaId, errorLocation)
      : null;

  return (
    <div className="kv">
      <span className="k">event id</span>
      <span className="v mono">#{event.id}</span>
      <span className="k">timestamp</span>
      <span className="v mono">
        {new Date(event.ts).toLocaleString()}{" "}
        <span className="muted">({event.ts})</span>
      </span>
      <span className="k">kind</span>
      <span className="v mono">{event.kind}</span>
      <span className="k">severity</span>
      <span className="v">
        <Pill tone={severityTone(event.severity)}>{event.severity}</Pill>
      </span>
      {serviceId !== undefined && (
        <>
          <span className="k">service</span>
          <span className="v mono">{String(serviceId)}</span>
        </>
      )}
      {(poolId !== undefined || poolName !== undefined) && (
        <>
          <span className="k">pool</span>
          <span className="v mono">
            {poolName !== undefined ? String(poolName) : ""}
            {poolId !== undefined ? ` (#${String(poolId)})` : ""}
          </span>
        </>
      )}
      {replicaId !== undefined && (
        <>
          <span className="k">replica</span>
          <span className="v mono">
            #{String(replicaId)}
            {typeof skyReplicaId === "number" && (
              <span className="muted"> · sky #{skyReplicaId}</span>
            )}
          </span>
        </>
      )}
      {typeof classification === "string" && (
        <>
          <span className="k">classification</span>
          <span className="v mono">{classification}</span>
        </>
      )}
      {typeof errorText === "string" && errorText !== "" && (
        <>
          <span className="k">error</span>
          <span
            className="v mono"
            style={{
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
              wordBreak: "break-word",
            }}
          >
            {errorText}
          </span>
        </>
      )}
      {/* The raised exception's class name. Shown unconditionally so
          an event whose `error` body is an empty string (seen in
          production for provisioning failures) still tells the
          operator *what* failed instead of rendering nothing. */}
      {typeof errorType === "string" && errorType !== "" && (
        <>
          <span className="k">error type</span>
          <span className="v mono">{errorType}</span>
        </>
      )}
      {typeof errorLocation === "string" && errorLocation !== "" && (
        <>
          <span className="k">at</span>
          <span
            className="v mono"
            style={{
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
              wordBreak: "break-word",
            }}
            title="Innermost source frame from the Python traceback that produced this error."
          >
            {errorLocation}
          </span>
        </>
      )}
      {typeof failureReason === "string" && failureReason !== "" && (
        <>
          <span className="k">failure reason</span>
          <span
            className="v mono muted"
            style={{
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
              wordBreak: "break-word",
            }}
          >
            {failureReason}
          </span>
        </>
      )}
      {typeof hint === "string" && hint !== "" && (
        <>
          <span className="k">hint</span>
          <span className="v">{hint}</span>
        </>
      )}
      {logsHref && (
        <>
          <span className="k">logs</span>
          <span className="v">
            <Link
              to={logsHref}
              className="btn btn--ghost btn--sm"
              title={
                typeof errorLocation === "string" && errorLocation !== ""
                  ? `Open the replica log filtered to ${errorLocation}`
                  : "Open the per-replica setup/run log on the Pool > Logs tab"
              }
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <Terminal size={12} />
              {typeof errorLocation === "string" && errorLocation !== ""
                ? "open logs at this frame"
                : "open replica logs"}
            </Link>
          </span>
        </>
      )}
      <span className="k">payload</span>
      <span className="v">
        <pre
          className="mono"
          style={{
            margin: 0,
            padding: 10,
            background: "var(--surface-1)",
            border: "1px solid var(--edge)",
            borderRadius: "var(--radius-md)",
            fontSize: 11.5,
            lineHeight: 1.5,
            whiteSpace: "pre-wrap",
            overflowWrap: "anywhere",
            wordBreak: "break-word",
            maxHeight: 360,
            overflowY: "auto",
          }}
        >
          {JSON.stringify(event.payload, null, 2)}
        </pre>
      </span>
    </div>
  );
}

/**
 * Build a Pool > Logs deep link for a given replica, optionally
 * pre-filling the search filter.
 *
 * The URL shape mirrors the rest of the Workloads page:
 * ``?focus=pool:<id>&tab=logs`` selects the right SubNav tab; we
 * layer ``replica=<sky_id>`` and ``q=<search>`` on top, both of
 * which ``PoolLogs`` reads on mount (and re-applies when the URL
 * changes).
 *
 * ``q`` carries the verbatim traceback frame text from the event
 * payload's ``error_location`` field. The Logs view does
 * case-insensitive substring filtering, so a value like
 * ``"server.py", line 142, in load_config`` matches the raw
 * traceback frame ``File "server.py", line 142, in load_config``
 * in the dump exactly — the operator lands on the offending frame
 * with one click instead of scrolling.
 */
function buildLogsHref(
  poolId: number,
  skyReplicaId: number,
  searchQuery: unknown,
): string {
  const sp = new URLSearchParams();
  sp.set("focus", `pool:${poolId}`);
  sp.set("tab", "logs");
  sp.set("replica", String(skyReplicaId));
  if (typeof searchQuery === "string" && searchQuery !== "") {
    sp.set("q", searchQuery);
  }
  return `/?${sp.toString()}`;
}

function ScopeChip({
  label,
  value,
  onRemove,
}: {
  label: string;
  value: string;
  onRemove: () => void;
}) {
  return (
    <span
      className="scope-chip"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "2px 4px 2px 8px",
        border: "1px solid var(--edge-strong)",
        background: "var(--surface-2)",
        borderRadius: "999px",
        fontSize: 11,
      }}
    >
      <span className="muted" style={{ textTransform: "uppercase", fontSize: 10 }}>
        {label}
      </span>
      <span className="mono">{value}</span>
      <button
        type="button"
        className="btn btn--ghost"
        onClick={onRemove}
        title={`Remove ${label} filter`}
        style={{ padding: 2, lineHeight: 0 }}
      >
        <X size={12} />
      </button>
    </span>
  );
}

function numOrNull(s: string | null): number | null {
  if (s === null || s === "") return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function labelForService(
  services: Service[] | undefined,
  serviceId: number | null,
): string | null {
  if (services === undefined || serviceId === null) return null;
  const svc = services.find((s) => s.id === serviceId);
  return svc?.name ?? null;
}

function labelForPool(
  pools: Pool[] | undefined,
  poolId: number | null,
): string | null {
  if (pools === undefined || poolId === null) return null;
  const pool = pools.find((p) => p.id === poolId);
  return pool?.sky_service_name ?? null;
}

function labelForReplica(
  replicas: Replica[] | undefined,
  replicaId: number | null,
): string | null {
  if (replicas === undefined || replicaId === null) return null;
  const r = replicas.find((rep) => rep.id === replicaId);
  if (!r) return null;
  return r.sky_replica_id ? `replica #${r.sky_replica_id}` : `#${replicaId}`;
}

/**
 * Pre-canned `kind` filter chips. The filter is a substring match
 * on `events.kind`, so the chip values are kind prefixes (e.g.
 * `cascade.` matches both `cascade.opened` and `cascade.closed`).
 *
 * The list intentionally maps onto the new event kinds emitted by
 * the per-pool SLO autoscaler / drain orchestrator / xDS server so
 * operators can isolate one subsystem at a time without typing.
 */
const KIND_FILTER_CHIPS: { value: string; label: string; hint: string }[] = [
  {
    value: "cascade.",
    label: "cascade",
    hint: "Routing cascade open/close events from the autoscaler.",
  },
  {
    value: "pool.scaled",
    label: "pool scaling",
    hint: "Per-pool scale-up / scale-down decisions.",
  },
  {
    value: "slo.",
    label: "SLO",
    hint: "SLO evaluation results and threshold crossings.",
  },
  {
    value: "replica.draining",
    label: "draining",
    hint: "Replica entered draining_long_lived or draining state.",
  },
  {
    value: "replica.terminated",
    label: "terminated",
    hint: "Replica was finally removed (after graceful drain).",
  },
  {
    value: "replica.eviction.outlier",
    label: "outlier eviction",
    hint: "Envoy outlier detection ejected a replica.",
  },
  {
    value: "xds.",
    label: "xDS",
    hint: "xDS push / ACK / NACK / timeout events.",
  },
  {
    value: "provisioning.",
    label: "provisioning",
    hint: "SkyPilot provisioning success / failure / quota / no-capacity.",
  },
  {
    value: "rollout.",
    label: "rollout",
    hint: "Blue/green rollout state machine transitions.",
  },
];

/**
 * One human-readable line for the stream row, so operators read
 * "Failed to acquire resources in all zones (capacity)" instead of a
 * truncated JSON blob. Preference order mirrors how the reconciler
 * populates payloads: the extracted error line, then the classified
 * failure reason, then the operator hint. Events whose error string
 * is empty (a real production shape for `provisioning.failed`) still
 * surface their `error_type` / `classification` so a failure never
 * renders blank.
 */
function humanEventMessage(e: EventRow): string | null {
  const p = e.payload ?? {};
  for (const key of ["error", "failure_reason", "reason", "message", "hint"]) {
    const v = p[key];
    if (typeof v === "string" && v.trim() !== "") return v;
  }
  const classification = p["classification"];
  const errorType = p["error_type"];
  const parts = [
    typeof classification === "string" && classification !== ""
      ? classification
      : null,
    typeof errorType === "string" && errorType !== "" ? errorType : null,
  ].filter(Boolean);
  if (parts.length > 0) return `${parts.join(" · ")} (no error body)`;
  return null;
}

/**
 * "Is this event self-healing or does it need a human?" chip.
 *
 * The event's own severity (info/warn/error) answers "how loud", not
 * "who acts". This chip answers the second question by looking the
 * event's classified kind up in the server-driven error-kind
 * catalogue (`GET /reference/error-kinds`):
 *
 *   - `transient`           → self-healing (amber): the reconciler
 *     retries forever; a `provisioning.failed / error` event every
 *     15 minutes is the retry loop working, not a fire.
 *   - `attention_external` / `attention_spec` → needs attention (red): a
 *     human must act (or at least verify).
 *
 * Unclassified events render no chip — inventing a triage level we
 * don't know would be exactly the dishonesty this UI is fixing.
 */
function EventAttentionChip({ event }: { event: EventRow }) {
  const catalog = useErrorKindReference();
  const p = event.payload ?? {};
  const classification = p["classification"];
  const kindKey =
    typeof classification === "string" && classification !== ""
      ? classification
      : event.kind;
  const meta = catalog.data?.error_kinds.find((k) => k.kind === kindKey);
  if (!meta) return null;
  if (meta.severity === "transient") {
    return (
      <Pill
        tone="warn"
        title={`${meta.label} — recovers via automatic retries; no action needed.`}
      >
        self-healing
      </Pill>
    );
  }
  return (
    <Pill
      tone="danger"
      title={
        meta.hint
          ? `${meta.label} — ${meta.hint}`
          : `${meta.label} — operator action required.`
      }
    >
      needs attention
    </Pill>
  );
}

function severityTone(sev: EventSeverity): PillTone {
  switch (sev) {
    case "warn":
      return "warn";
    case "error":
    case "critical":
      return "danger";
    case "info":
      return "info";
    case "debug":
      return "neutral";
    default:
      return "ok";
  }
}
